/**
 * Alpine.js Multi-Select Editor Component
 * Provides inline editing functionality for multi-select checkbox fields
 */

import { editorBase } from './alpine-editor-base.js';

/**
 * Multi-Select Editor Component Factory
 * Handles fields like "Looking for" with multiple checkbox options
 */
export function multiselectEditor() {
    return {
        // State
        isEditing: false,
        selectedValues: [],
        originalValues: [],
        isSaving: false,
        error: null,
        
        // Configuration
        fieldName: '',
        options: [], // Array of {value, label} objects
        endpoint: '',
        
        // Computed properties
        get hasChanges() {
            // Check if arrays have same values (order doesn't matter)
            if (this.selectedValues.length !== this.originalValues.length) {
                return true;
            }
            return !this.selectedValues.every(val => this.originalValues.includes(val));
        },
        
        get displayValue() {
            if (!this.selectedValues || this.selectedValues.length === 0) {
                return '';
            }
            
            // Get labels for selected values
            const selectedLabels = this.selectedValues
                .map(value => {
                    const option = this.options.find(opt => opt.value === value);
                    return option ? option.label : value;
                })
                .filter(label => label);
            
            return selectedLabels.join(', ');
        },
        
        // Methods
        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            
            // Parse options from data attribute
            try {
                this.options = JSON.parse(element.dataset.options || '[]');
            } catch (e) {
                console.error('Failed to parse options for', this.fieldName, e);
                this.options = [];
            }
            
            // Get endpoint
            this.endpoint = element.dataset.endpoint || editorBase.getEndpointForField(this.fieldName);
            
            // Get initial values from element's data attributes
            let rawValue = element.dataset.rawValue || element.dataset.value || '';
            
            // If no value on element, check for .inline-edit-content child (legacy)
            if (!rawValue) {
                const contentEl = element.querySelector('.inline-edit-content');
                if (contentEl) {
                    rawValue = contentEl.dataset.rawValue || contentEl.dataset.value || '';
                }
            }
            
            // Parse comma-separated values
            if (rawValue) {
                this.originalValues = rawValue.split(',').filter(v => v.trim());
            } else {
                this.originalValues = [];
            }
            
            this.selectedValues = [...this.originalValues];
            
            // Initialize base utilities
            editorBase.initEditor(this);
        },
        
        toggleValue(value) {
            const index = this.selectedValues.indexOf(value);
            if (index > -1) {
                this.selectedValues.splice(index, 1);
            } else {
                this.selectedValues.push(value);
            }
        },
        
        isSelected(value) {
            return this.selectedValues.includes(value);
        },
        
        startEdit() {
            this.isEditing = true;
            this.error = null;
            
            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }
        },
        
        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            
            try {
                // Use the base editor's saveField method which handles auth properly
                const result = await editorBase.saveField(
                    this.endpoint,
                    this.fieldName,
                    this.selectedValues.join(',')
                );
                
                if (result.error) {
                    throw new Error(result.message || 'Failed to save');
                }
                
                // Update original values
                this.originalValues = [...this.selectedValues];
                
                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, this.selectedValues.join(','));
                }
                
                // Exit edit mode
                this.completeEdit();
                
            } catch (error) {
                console.error('Save error:', error);
                this.error = error.message || 'Failed to save. Please try again.';
            } finally {
                this.isSaving = false;
            }
        },
        
        cancel() {
            if (this.isSaving) {
                return;
            }
            
            // Reset to original values
            this.selectedValues = [...this.originalValues];
            this.error = null;
            
            this.completeEdit();
        },
        
        completeEdit() {
            this.isEditing = false;
            
            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },
        
        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.cancel();
            }
        }
    };
}